<template>
    <Transition name="fade">
        <PermissionDialog :is-open="showMicPermissionAide" @cancel="handleDialogCancel" />
    </Transition>
    <RecordingTestDialog @dismiss="usingText = true" @mic-selected="handleMicSelection" />
    <div class="w-full flex flex-col gap-6 md:gap-10">
        <div v-if="!isAnswered" class="flex flex-col gap-3 max-w-[440px] mx-auto text-center">
            <p class="text-ink-muted body-small">{{ currentQuestion.title }}</p>
            <h2 class="title-subsection text-ink">{{ currentQuestion.text }}</h2>
        </div>
        <ChatNotification v-if="error" class="md:max-w-[800px] mx-auto" :message="error.message" theme="error" @dismiss="$emit('dismiss-error')" />
        <div
            class="md:max-w-[800px] w-full mx-auto grow bg-surface-raised flex md:flex-row flex-wrap flex-col items-start md:items-center gap-4 md:gap-1 justify-between rounded-3xl px-6 transition-color border-2"
            :class="[isRecording ? 'py-5' : 'py-6', isAnswered && !isLoading ? 'border-border-strong' : 'border-control-border']">
            <template v-if="showLoader">
                <div class="text-left h-10 body-small text-ink-muted flex items-center gap-3">{{ t("onboarding.questions.thinking") }} <LoadingSpinner class="h-4" /></div>
            </template>
            <template v-else-if="isAnswered">
                <div class="grow flex flex-col gap-10 justify-between">
                    <div class="grow title-body text-ink">
                        <template v-if="currentQuestion.offTopic">{{ t("onboarding.questions.offTopic") }}</template>
                        <template v-else>
                            {{ currentQuestion.llmResponse }}
                        </template>
                    </div>
                    <div class="body-small text-ink-muted flex flex-row-reverse md:flex-row items-center justify-between">
                        <div class="hidden md:block">{{ t("onboarding.questions.confirmPrompt") }}</div>
                        <BaseButton theme="tertiary" size="sm" class="flex gap-1 items-center" @click="handleMoreContext">
                            <i v-if="showTextInput" class="bi bi-pencil text-xs" aria-hidden="true" />
                            <span v-else class="h-8 w-8">
                                <MicrophoneCircle background-class="fill-transparent" foreground-class="stroke-[currentColor]" />
                            </span>
                            <template v-if="currentQuestion.offTopic">{{ t("onboarding.questions.editResponseButton") }}</template>
                            <template v-else>{{ t("onboarding.questions.addContextButton") }}</template>
                        </BaseButton>
                    </div>
                </div>
            </template>

            <template v-else>
                <div class="grow w-full md:w-auto">
                    <BaseTextarea
                        v-if="showTextInput"
                        ref="baseTextareaRef"
                        v-model:input="text"
                        :placeholder="t('onboarding.questions.answerPlaceholder')"
                        :disabled="disabled"
                        class="body"
                        :aria-label="t('onboarding.questions.answerLabel')"
                        @keydown.enter="handleTextEnter"></BaseTextarea>
                    <div v-else class="w-full flex items-center gap-6">
                        <div class="w-full md:w-4/5">
                            <VoiceLevels :class="{ 'opacity-40': !isRecording }" class="py-4" :bar-size="isRecording ? BAR_SIZE.SMALL : BAR_SIZE.TINY" :levels="levelHistory" />
                        </div>
                        <div v-if="isRecording" class="text-ink-muted body-small">
                            <template v-if="isSilent">{{ t("onboarding.questions.noSound") }}</template>
                            <template v-else>{{ t("onboarding.questions.listening") }}</template>
                        </div>
                    </div>
                </div>
                <!-- New mode: mic shrinks when text is present; send grows from 0 -->
                <div v-if="betterVoiceComposerEnabled" class="self-end flex items-center gap-2">
                    <template v-if="isRecording">
                        <BaseButton
                            data-testid="record-cancel"
                            theme="tertiary"
                            icon-only
                            :title="t('common.cancel')"
                            class="h-10 w-10 flex items-center justify-center"
                            @click="cancelRecording">
                            <i class="bi bi-x-lg text-sm" aria-hidden="true" />
                        </BaseButton>
                        <BaseButton
                            theme="primary"
                            icon-only
                            data-testid="record-stop"
                            :title="t('onboarding.questions.sendRecordingButton')"
                            class="shrink-0 flex items-center justify-center h-10 w-10 rounded-full"
                            @click="stopRecording">
                            <i class="bi bi-send-fill text-lg rotate-45" aria-hidden="true" />
                        </BaseButton>
                    </template>
                    <template v-else>
                        <BaseButton
                            v-if="voiceInputEnabled"
                            :disabled="disabled"
                            theme="primary"
                            icon-only
                            :aria-label="t('onboarding.questions.recordVoiceButton')"
                            :title="t('onboarding.questions.recordVoiceButton')"
                            data-testid="record-voice"
                            class="shrink-0 flex items-center justify-center rounded-full transition-[width,height,background-color,opacity] duration-300 ease-out"
                            :class="text ? 'h-10 w-10' : 'h-12 w-12'"
                            @click="handleMicStart">
                            <i class="bi bi-mic-fill transition-[font-size] duration-300" :class="text ? 'text-lg' : 'text-2xl'" aria-hidden="true" />
                        </BaseButton>
                        <BaseButton
                            :disabled="!text || disabled"
                            theme="primary"
                            icon-only
                            data-testid="text-submit"
                            :title="t('onboarding.questions.submitButton')"
                            class="shrink-0 flex items-center justify-center rounded-full transition-[width,height,opacity,transform,background-color] duration-300 ease-out overflow-hidden"
                            :class="submitSizeClass"
                            @click="handleText">
                            <i class="bi bi-send-fill text-base rotate-45" aria-hidden="true" />
                        </BaseButton>
                    </template>
                </div>
                <!-- Old mode: existing toggle + button layout -->
                <div v-else class="self-end transition-color flex md:justify-between items-center" :class="[hasAnswer ? 'gap-3' : 'gap-6', showTextInput ? 'self-end' : '']">
                    <template v-if="showTextInput">
                        <BaseButton
                            v-if="voiceInputEnabled"
                            data-testid="voice-toggle"
                            theme="tertiary"
                            size="sm"
                            :aria-label="t('onboarding.questions.switchToVoice')"
                            class="flex gap-1 items-center"
                            @click="usingText = false">
                            <span class="h-8 w-8">
                                <MicrophoneCircle background-class="fill-transparent" foreground-class="stroke-[currentColor]" />
                            </span>
                            <template v-if="!text"
                                ><span class="hidden md:inline">{{ t("onboarding.questions.switchToVoice") }}</span
                                ><span class="md:hidden">{{ t("onboarding.questions.switchToVoiceShort") }}</span></template
                            >
                        </BaseButton>
                        <BaseButton
                            data-testid="text-submit"
                            :title="t('onboarding.questions.submitButton')"
                            :disabled="!text || disabled"
                            theme="primary"
                            icon-only
                            class="h-10 w-10 rounded-full"
                            @click="handleText">
                            <i class="bi bi-arrow-up" aria-hidden="true" />
                        </BaseButton>
                    </template>
                    <template v-else>
                        <template v-if="isRecording">
                            <BaseButton data-testid="record-cancel" theme="tertiary" icon-only size="sm" :title="t('common.cancel')" @click="cancelRecording">
                                <i class="bi bi-x-lg text-sm" aria-hidden="true" />
                            </BaseButton>
                            <BaseButton theme="primary" size="sm" data-testid="record-stop" class="rounded-3xl flex items-center gap-1" @click="stopRecording">
                                <span class="h-6 w-6">
                                    <StopCircle background-class="fill-transparent" foreground-class="fill-[currentColor]" />
                                </span>
                                {{ t("onboarding.questions.stopButton") }}
                            </BaseButton>
                        </template>
                        <template v-else>
                            <BaseButton data-testid="text-toggle" theme="tertiary" size="sm" :disabled="isRecording" class="flex gap-3 items-center" @click="usingText = true">
                                <span class="h-4 w-4">
                                    <Keyboard foreground-class="fill-[currentColor]" />
                                </span>
                                <template v-if="!isRecording"
                                    ><span class="hidden md:inline">{{ t("onboarding.questions.switchToText") }}</span
                                    ><span class="md:hidden">{{ t("onboarding.questions.switchToTextShort") }}</span></template
                                >
                            </BaseButton>
                            <BaseButton data-testid="record-start" theme="primary" size="sm" class="rounded-3xl flex items-center gap-1" @click="startRecording">
                                <span class="h-6 w-6">
                                    <MicrophoneCircle background-class="fill-transparent" foreground-class="stroke-[currentColor]" />
                                </span>
                                {{ t("onboarding.questions.talkButton") }}
                            </BaseButton>
                        </template>
                    </template>
                </div>
            </template>
        </div>

        <Transition name="fade">
            <div v-if="isAnswered && !currentQuestion.offTopic" class="inline-block mx-auto">
                <BaseButton theme="primary" :disabled="isLoading" @click="handleNextQuestion">{{ t("onboarding.questions.continueButton") }}</BaseButton>
            </div>
            <template v-else-if="!isAnswered && !isLoading && !error">
                <ContextMeterText v-if="showTextInput && hasAnswer" :text="text" />
                <ContextMeter v-else-if="isRecording" :running="contextMeterRunning" />
            </template>
        </Transition>
    </div>
</template>

<script setup>
import { AudioRecorder } from "/js/AudioRecorder.js";
import { transcribeRecording } from "/js/transcription.js";
import { useThrottleFn } from "@vueuse/core";
import ChatNotification from "~vue/ChatNotification.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import VoiceLevels, { BAR_SIZE } from "~vue/components/VoiceLevels.vue";
import { useMicConsent } from "~vue/composables/useMicConsent";
import { CHAT_EVENT } from "~vue/events";
import Keyboard from "~vue/icons/Keyboard.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import MicrophoneCircle from "~vue/icons/MicrophoneCircle.vue";
import StopCircle from "~vue/icons/StopCircle.vue";
import ContextMeter from "~vue/Onboarding/ContextMeter.vue";
import ContextMeterText from "~vue/Onboarding/ContextMeterText.vue";
import RecordingTestDialog from "~vue/RecordingTestDialog.vue";
import { isInIFrame } from "~vue/utils";
import { unlessComposing } from "~vue/utils/keyboard";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import PermissionDialog from "./PermissionDialog.vue";

const SILENCE_TIMEOUT_MS = 5000;

const props = defineProps({
    error: { type: Error, default: null },
    disabled: { type: Boolean, default: false },
    currentQuestion: { type: Object, required: true },
    isLoading: { type: Boolean, default: false },
    betterVoiceComposerEnabled: { type: Boolean, default: false },
    hideChatVoiceInputEnabled: { type: Boolean, default: false },
});

const emit = defineEmits(["answer", "dismiss-error", "next", "transcribe-error"]);

const { t } = useI18n();

const { emitter } = inject("globalProperties");
const organizationDetails = inject("organization_details", null);
const { micConsentBlocks } = useMicConsent();

const STATE = {
    QUESTION: "question",
    RECORDING: "recording",
    TRANSCRIBING: "transcribing",
    ANSWER: "answer",
};

const baseTextareaRef = useTemplateRef("baseTextareaRef");
const contextMeterRunning = ref(false);
const recordingLevelSamplingRate = ref(100);
const text = ref("");
const voiceInputEnabled = computed(() => !props.hideChatVoiceInputEnabled && !organizationDetails?.value?.is_chat_voice_input_hidden);

// Default to text mode when the new voice composer is enabled (mic button starts recording directly)
const usingText = ref(props.betterVoiceComposerEnabled || !voiceInputEnabled.value);

// With no mic there is nothing to switch back from, so the question always answers by text.
const showTextInput = computed(() => usingText.value || !voiceInputEnabled.value);
const state = ref(STATE.QUESTION);
const showMicPermissionAide = ref(false);
const recorder = ref(new AudioRecorder({ levelCheckIntervalMs: 100 }));
const levelHistory = ref([]);
const silenceTimeout = ref(null);

const isRecording = computed(() => state.value === STATE.RECORDING);
const isTranscribing = computed(() => state.value === STATE.TRANSCRIBING);
const isAnswered = computed(() => state.value === STATE.ANSWER);
const showLoader = computed(() => isTranscribing.value || props.isLoading);
const isSilent = computed(() => silenceTimeout.value !== null);
const isProcessing = computed(() => isRecording.value || isTranscribing.value);
const shouldAppend = computed(() => !!props.currentQuestion.answer && !props.currentQuestion.offTopic);

// The submit button normally grows out of zero width as the mic shrinks. With no mic to trade
// places with, it holds the mic's resting size instead.
const submitSizeClass = computed(() => {
    if (!voiceInputEnabled.value) {
        return "h-12 w-12 opacity-100 scale-100";
    }
    return text.value ? "h-10 w-10 opacity-100 scale-100" : "h-0 w-0 p-0 opacity-0 scale-0";
});

const hasAnswer = computed(() => {
    if (showTextInput.value) {
        return text.value.length > 0;
    }

    return isRecording.value;
});

watch(usingText, (value) => {
    if (!value) {
        return;
    }

    nextTick(focusTextInput);
});

watch(isRecording, (value) => {
    if (value) {
        emit("dismiss-error");
    }
});

watch(
    () => props.currentQuestion,
    () => {
        state.value = STATE.QUESTION;
        text.value = "";
    },
);

watch(state, (value) => {
    if (value === STATE.QUESTION && usingText.value) {
        nextTick(focusTextInput);
    }
});

watch(
    () => props.error,
    (value) => {
        if (value) {
            handleErrorFallback();
        }
    },
);

onMounted(() => {
    recorder.value.on("level", handleRecorderLevel);
    recorder.value.on("finish", (data) => handleRecorderFinished(data));
    recorder.value.on("start", handleRecorderStart);
    recorder.value.on("stop", handleRecorderStop);
    recorder.value.on("silence", handleSilence);
});

onUnmounted(() => {
    if (recorder.value) {
        clearRecorder();
    }
});

function focusTextInput() {
    baseTextareaRef.value?.textareaRef?.focus();
}

function handleErrorFallback() {
    state.value = STATE.QUESTION;
    usingText.value = true;
}

function handleDialogCancel() {
    showMicPermissionAide.value = false;
    usingText.value = true;
    logUserInteraction("use_text", {});
}

async function startRecording() {
    if (!voiceInputEnabled.value) {
        return;
    }

    if (micConsentBlocks(startRecording)) {
        return;
    }

    try {
        let viewedPrompt = false;
        await recorder.value.start((micPermissionState) => {
            if (micPermissionState === "prompt" || isInIFrame()) {
                showMicPermissionAide.value = true;
                viewedPrompt = true;
            }
        });

        /*
         *Reset any draft text that may have existed.before switching
         * to voice
         */
        if (text.value) {
            text.value = "";
        }

        if (viewedPrompt) {
            // User allowed mic after prompt if it doesn't error above
            logUserInteraction("allowed_mic", {});

            /*
             * User may have chosen "I'd rather type" in the permission dialog
             * but still grant permission in the browser prompt. In this case,
             * we should not continue with a recording because the user
             * chose to use text.
             */
            if (usingText.value) {
                recorder.value.stop({ abort: true });
                return;
            }
        }
    } catch {
        usingText.value = true;
        openTestDialog();
        logUserInteraction("use_text", {});
    } finally {
        if (showMicPermissionAide.value) {
            showMicPermissionAide.value = false;
        }
    }
}

function clearRecorder() {
    recorder.value.off("level", handleRecorderLevel);
    recorder.value.off("finish", (data) => handleRecorderFinished(data));
    recorder.value.off("start", handleRecorderStart);
    recorder.value.off("stop", handleRecorderStop);
    recorder.value.off("silence", handleSilence);
}

function stopRecording() {
    recorder.value.stop();
}

function cancelRecording() {
    recorder.value.stop({ abort: true });
    levelHistory.value = [];
    state.value = STATE.QUESTION;
    if (props.betterVoiceComposerEnabled) {
        // In the new composer, return to text mode so the textarea + mic+send buttons reappear
        usingText.value = true;
    }
}

async function handleMicStart() {
    usingText.value = false;
    await startRecording();
}

function handleText() {
    emit("answer", {
        answer: text.value,
        append: shouldAppend,
        onFinish: () => {
            state.value = STATE.ANSWER;
        },
        usingMic: false,
    });
}

const handleTextEnter = unlessComposing(handleText);

function handleRecorderStart() {
    state.value = STATE.RECORDING;
}

async function handleRecorderFinished({ blob }) {
    levelHistory.value = [];
    state.value = STATE.TRANSCRIBING;

    try {
        const transcript = await transcribeRecording({
            blob,
        });
        /*
         * Persist transcript as text answer in case there is an upstream
         * error when submitting answer.
         */
        text.value = transcript;

        emit("answer", {
            answer: transcript,
            append: shouldAppend,
            onFinish: () => {
                state.value = STATE.ANSWER;
            },
            usingMic: true,
        });
    } catch (e) {
        const error = new Error(t("onboarding.questions.transcribeError"));
        emit("transcribe-error", error);
        logError(e);
    }
}

function openTestDialog() {
    emitter.emit(CHAT_EVENT.OPEN_MIC_TEST_DIALOG);
}

function clearSilenceTimeout() {
    if (silenceTimeout.value) {
        window.clearTimeout(silenceTimeout.value);
        silenceTimeout.value = null;
    }
}

function handleRecorderStop() {
    contextMeterRunning.value = false;
    clearSilenceTimeout();
}

function handleSilence() {
    silenceTimeout.value = window.setTimeout(() => {
        cancelRecording();
        openTestDialog();
    }, SILENCE_TIMEOUT_MS);
}

function handleNextQuestion() {
    emit("next");
}

function handleMoreContext() {
    if (showTextInput.value) {
        text.value = "";
        state.value = STATE.QUESTION;
    } else {
        startRecording();
    }

    logUserInteraction("give_more_context_clicked", {});
}

const updateContextMeterSampling = useThrottleFn(({ isBelowSilenceThreshold }) => {
    /*
     * Check whether user is silent. Context meter should only run
     * when we detect signal.
     * If silence is detected, then the sampling rate is
     * increased so that signal is detected sooner. If signal is detected,
     * the sampling rate is decreased to accomodate natural pausing.
     */
    contextMeterRunning.value = !isBelowSilenceThreshold;
    if (isBelowSilenceThreshold) {
        recordingLevelSamplingRate.value = 200;
    } else {
        recordingLevelSamplingRate.value = 4000;
    }
}, recordingLevelSamplingRate);

function handleRecorderLevel(level) {
    const isBelowSilenceThreshold = recorder.value.isBelowSilenceThreshold(level);
    if (!isBelowSilenceThreshold) {
        clearSilenceTimeout();
    }
    levelHistory.value.push(level);

    updateContextMeterSampling({ isBelowSilenceThreshold });
}

function handleMicSelection(event) {
    recorder.value?.setAudioInputDeviceId(event.deviceId);
}

defineExpose({ handleMicSelection, cancelRecording, isProcessing, isAnswered });
</script>

<style scoped>
@layer components {
    textarea {
        -ms-overflow-style: none;
        scrollbar-width: none;
    }

    textarea::-webkit-scrollbar,
    textarea::-webkit-resizer {
        display: none;
    }
}
</style>
